import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { FILE_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { dmY } from "@/lib/dateFmt";
import useAutoRefresh from "@/lib/useAutoRefresh";
import LastUpdatedChip from "@/components/LastUpdatedChip";
import { UploadSimple, ClockCounterClockwise, Files } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

/**
 * v1.27.67 — Customer Uploads hub.
 * Combines the two upload-related screens under a single tab:
 *   • Recent Uploads (last 5, auto-refresh) — was on the old dashboard
 *   • Upload History (full grid of every slip) — was /portal/my-uploads
 * Plus a prominent "Upload Payment Slip" CTA at the top.
 * No backend changes; existing /api/dashboard/customer + /api/uploads endpoints.
 */
export default function CustomerUploadsHub() {
    // Recent (top-5) via dashboard endpoint so pending → verified transitions
    // stay in sync with the Home screen.
    const { data: dash, lastUpdated, refresh, isRefreshing } = useAutoRefresh(
        () => api.get("/dashboard/customer").then((r) => r.data),
        { intervalMs: 30_000 },
    );
    const recent = (dash?.recent_uploads || []).slice(0, 5);

    // Full history — one-shot fetch, mirrors the old MyUploads page.
    const [history, setHistory] = useState([]);
    useEffect(() => { api.get("/uploads").then((r) => setHistory(r.data || [])); }, []);

    return (
        <div className="space-y-8" data-testid="customer-uploads-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Portal</div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Uploads</h1>
                    <p className="mt-1 text-sm text-slate-500">Upload a new payment slip or review previous ones.</p>
                </div>
                <Link to="/portal/upload">
                    <Button data-testid="uploads-hub-upload-btn" className="bg-blue-600 text-white hover:bg-blue-700">
                        <UploadSimple size={18} className="mr-2" /> Upload Payment Slip
                    </Button>
                </Link>
            </div>

            {/* Recent Uploads */}
            <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="uploads-hub-recent">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                    <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
                        <ClockCounterClockwise size={20} weight="duotone" className="text-slate-500" />
                        Recent Uploads
                    </h2>
                    <LastUpdatedChip lastUpdated={lastUpdated} isRefreshing={isRefreshing} onRefresh={refresh} testId="uploads-hub-recent-updated" />
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                <th className="px-5 py-3 font-medium">Date</th>
                                <th className="px-5 py-3 font-medium">UTR</th>
                                <th className="px-5 py-3 text-right font-medium">Amount</th>
                                <th className="px-5 py-3 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.length === 0 ? (
                                <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">No recent uploads</td></tr>
                            ) : recent.map((u) => (
                                <tr key={u.id} className="border-t border-slate-100" data-testid={`uploads-hub-recent-row-${u.id}`}>
                                    <td className="px-5 py-3 text-slate-600">{dmY(u.payment_date || u.uploaded_at)}</td>
                                    <td className="px-5 py-3 font-mono text-xs text-slate-700">{u.utr || "—"}</td>
                                    <td className="px-5 py-3 text-right tabular-nums text-slate-900">{fmt(u.amount)}</td>
                                    <td className="px-5 py-3"><StatusBadge status={u.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Upload History */}
            <section className="space-y-3" data-testid="uploads-hub-history">
                <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
                    <Files size={20} weight="duotone" className="text-slate-500" />
                    Upload History
                    {history.length > 0 && (
                        <span className="text-xs font-normal text-slate-500">· {history.length} total</span>
                    )}
                </h2>
                {history.length === 0 ? (
                    <EmptyState
                        icon={UploadSimple}
                        title="No slips uploaded yet"
                        description="Your uploaded payment slips will appear here with verification status."
                        action={<Link to="/portal/upload"><Button className="mt-2 bg-blue-600 text-white hover:bg-blue-700">Upload a slip</Button></Link>}
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {history.map((u) => (
                            <div key={u.id} data-testid={`my-upload-${u.id}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                                <a href={`${FILE_BASE}${u.file_url}`} target="_blank" rel="noreferrer">
                                    <img
                                        src={`${FILE_BASE}${u.file_url}`}
                                        alt="slip"
                                        className="h-40 w-full bg-slate-100 object-cover"
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                </a>
                                <div className="space-y-2 p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="font-display text-lg font-bold tabular-nums text-slate-900">{fmt(u.amount)}</div>
                                        <StatusBadge status={u.status} />
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        <div>Date: <span className="text-slate-700">{dmY(u.payment_date)}</span></div>
                                        <div>UTR: <span className="font-mono text-slate-700">{u.utr || "—"}</span></div>
                                        <div>App: <span className="text-slate-700">{u.payment_app || "—"}</span></div>
                                    </div>
                                    {u.verification_notes && (
                                        <div className="rounded-md bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
                                            <span className="font-medium">Admin note:</span> {u.verification_notes}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
